/**
 * Phone / OS notification shade — the banners our service workers showed
 * (FCM firebase-messaging-sw.js and Web Push sw.js). Deleting or reading in
 * the app must clear them too; otherwise Android keeps the old stack and
 * re-shows it with the next push. Every call is best-effort.
 */

async function shownNotifications(): Promise<Notification[]> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return []
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    const lists = await Promise.all(
      registrations.map((registration) => registration.getNotifications().catch(() => [] as Notification[])),
    )
    return lists.flat()
  } catch {
    return []
  }
}

/** Close shade banners whose tag passes `match` (no-op when none are showing). */
export async function closeShadeNotifications(match: (tag: string) => boolean): Promise<void> {
  for (const shown of await shownNotifications()) {
    if (match(shown.tag)) shown.close()
  }
}

export function closeShadeTags(tags: Iterable<string>): Promise<void> {
  const set = new Set(tags)
  if (!set.size) return Promise.resolve()
  return closeShadeNotifications((tag) => set.has(tag))
}

export function closeAllShadeNotifications(): Promise<void> {
  return closeShadeNotifications(() => true)
}

/** Tags of the banners showing right now (empty when none / unsupported). */
export async function shadeTags(): Promise<string[]> {
  return (await shownNotifications()).map((shown) => shown.tag)
}
